'use client';

/**
 * Integration OAuth client panel.
 *
 * One reusable component for the four tile states the integration hub can
 * render for a static-client OAuth integration:
 *
 *   1. `available` + platform env configured (most providers): primary
 *      Connect, BYO hidden under "Use my own OAuth app".
 *   2. `available` + no platform env (e.g. PayPal in a fresh deploy):
 *      secondary-styled Connect that flips to primary once BYO is saved.
 *   3. `available` + DCR-only (Vercel/Linear/Notion): primary Connect, no
 *      Advanced section. (We render a no-op variant of this panel.)
 *   4. `restricted_allowlist` (Figma): waitlist link only. No Connect.
 *   5. `coming_soon`: greyed-out informational only.
 *
 * Discord/Telegram `byo_app` flows are orthogonal — they keep their
 * existing app-credentials form. This panel is only for swapping the
 * OAuth client_id/secret used by `oauth2`-type integrations.
 *
 * Connect is rendered by the parent (existing CTAs in `integration-hub`);
 * this panel only handles the BYO toggle + form. The parent passes a
 * `connectButtonStyle` callback so the panel can ask the parent to switch
 * Connect from primary → secondary when no platform env is available and
 * no BYO is saved.
 */

import { useEffect, useState, useCallback } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useT } from '@/components/i18n-provider';

interface ByoState {
  purpose: 'mcp' | 'messaging' | 'posting';
  providerId: string;
  providerLabel: string;
  availability: 'available' | 'restricted_allowlist' | 'coming_soon';
  hasPlatformEnv: boolean;
  hasByo: boolean;
  preference: 'platform' | 'byo';
  redirectUri: string;
  registrationUrl?: string;
  byoClientIdMask?: string | null;
}

interface PanelProps {
  agentConfigId: string;
  integrationId: string;
  mode?: string;
  /**
   * Notify the parent when the panel's view of "is the Connect button
   * primary or secondary" changes. Parent re-renders Connect accordingly.
   *   true  -> Connect is primary (env or saved BYO)
   *   false -> Connect is secondary (no env, no BYO)
   */
  onConnectStyleChange?: (isPrimary: boolean) => void;
  /** Called after a successful save / delete so the parent can refetch. */
  onChanged?: () => void;
}

export function IntegrationOAuthClientPanel({
  agentConfigId,
  integrationId,
  mode = 'oauth',
  onConnectStyleChange,
  onChanged,
}: PanelProps) {
  const t = useT();
  const [state, setState] = useState<ByoState | null>(null);
  const [loading, setLoading] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/agents/${agentConfigId}/integrations/${integrationId}/oauth-client?mode=${encodeURIComponent(mode)}`,
      );
      if (res.status === 400) {
        const j = await res.json().catch(() => ({}));
        if (j?.error === 'BYO not supported for identity providers') {
          setState(null);
          return;
        }
      }
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        if (j?.error) setError(String(j.error));
        setState(null);
        return;
      }
      const json = (await res.json()) as ByoState;
      setState(json);
      const isPrimary = json.hasPlatformEnv || json.hasByo;
      onConnectStyleChange?.(isPrimary);
      // Inline form is auto-shown when no env AND no BYO yet — that's
      // the "Case B" path from the plan.
      if (!json.hasPlatformEnv && !json.hasByo) setFormOpen(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [agentConfigId, integrationId, mode, onConnectStyleChange]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const save = useCallback(
    async (payload: {
      clientId: string;
      clientSecret?: string;
      scope?: string;
      preference?: 'platform' | 'byo';
    }) => {
      setSaving(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/agents/${agentConfigId}/integrations/${integrationId}/oauth-client?mode=${encodeURIComponent(mode)}`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(payload),
          },
        );
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          setError(String(j?.error ?? `save_${res.status}`));
          return;
        }
        await refetch();
        setFormOpen(false);
        onChanged?.();
      } finally {
        setSaving(false);
      }
    },
    [agentConfigId, integrationId, mode, refetch, onChanged],
  );

  const removeByo = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/agents/${agentConfigId}/integrations/${integrationId}/oauth-client?mode=${encodeURIComponent(mode)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(String(j?.error ?? `remove_${res.status}`));
        return;
      }
      await refetch();
      onChanged?.();
    } finally {
      setSaving(false);
    }
  }, [agentConfigId, integrationId, mode, refetch, onChanged]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner size="sm" tone="muted" />
        <span>{t('agentTools.loadingCredentialSettings')}</span>
      </div>
    );
  }

  if (!state) {
    // Identity / non-OAuth providers: render nothing.
    return null;
  }

  if (state.availability === 'restricted_allowlist') {
    return (
      <div className="rounded-md border border-amber-300/50 bg-amber-50/40 p-3 text-xs space-y-1">
        <div className="font-medium text-amber-900">
          {t('agentTools.oauthAllowlistTitle', { provider: state.providerLabel })}
        </div>
        <p className="text-amber-900/80">
          {t('agentTools.oauthAllowlistBody', { provider: state.providerLabel })}
        </p>
        {state.registrationUrl && (
          <a
            href={state.registrationUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-block underline text-amber-900"
          >
            {t('agentTools.oauthJoinWaitlist', { provider: state.providerLabel })} ↗
          </a>
        )}
      </div>
    );
  }

  if (state.availability === 'coming_soon') {
    return (
      <div className="rounded-md border border-border p-3 text-xs text-muted-foreground">
        {t('agentTools.notYetConnectable', { provider: state.providerLabel })}
      </div>
    );
  }

  // Case A: platform env present. BYO under Advanced.
  if (state.hasPlatformEnv) {
    return (
      <div className="space-y-2 text-xs">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        >
          {advancedOpen ? t('settings.nsfwHide') : t('settings.nsfwShow')} {t('agentTools.advanced')} ▾
        </button>
        {advancedOpen && (
          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="text-[11px] text-muted-foreground">
              {t('agentTools.oauthSharedAppNote', { provider: state.providerLabel })}
            </div>
            {state.hasByo ? (
              <div className="space-y-2">
                <div className="text-[11px]">
                  <span className="font-medium">{t('agentTools.byoClient')}</span>{' '}
                  <code className="font-mono">
                    {state.byoClientIdMask ?? t('agentTools.byoClientSet')}
                  </code>
                </div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1 text-[11px]">
                    <input
                      type="radio"
                      name="oauthPreference"
                      checked={state.preference === 'platform'}
                      disabled={saving}
                      onChange={() =>
                        save({
                          clientId: '',
                          preference: 'platform',
                        }).catch(() => {})
                      }
                    />{' '}
                    {t('agentTools.useRobutlerApp')}
                  </label>
                  <label className="inline-flex items-center gap-1 text-[11px]">
                    <input
                      type="radio"
                      name="oauthPreference"
                      checked={state.preference === 'byo'}
                      disabled={saving}
                      onChange={() =>
                        save({
                          clientId: '',
                          preference: 'byo',
                        }).catch(() => {})
                      }
                    />{' '}
                    {t('agentTools.useMyOwnApp')}
                  </label>
                </div>
                <button
                  type="button"
                  onClick={removeByo}
                  disabled={saving}
                  className="text-[11px] text-destructive hover:underline"
                >
                  {t('agentTools.removeMyCredentials')}
                </button>
              </div>
            ) : formOpen ? (
              <ByoForm
                state={state}
                saving={saving}
                onCancel={() => setFormOpen(false)}
                onSave={(payload) => save({ ...payload, preference: 'byo' })}
              />
            ) : (
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                className="text-[11px] underline"
              >
                {t('agentTools.useMyOwnOauthApp')}
              </button>
            )}
            {error && <div className="text-[11px] text-destructive">{error}</div>}
          </div>
        )}
      </div>
    );
  }

  // Case B: no platform env. Inline BYO form.
  return (
    <div className="rounded-md border border-border p-3 text-xs space-y-2">
      {state.hasByo ? (
        <div className="space-y-2">
          <div>
            <span className="font-medium">{t('agentTools.byoClient')}</span>{' '}
            <code className="font-mono">{state.byoClientIdMask ?? t('agentTools.byoClientSet')}</code>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="text-[11px] underline"
            >
              {t('agentTools.replaceCredentials')}
            </button>
            <button
              type="button"
              onClick={removeByo}
              disabled={saving}
              className="text-[11px] text-destructive hover:underline"
            >
              {t('actions.remove')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-[11px] text-muted-foreground">
            {t('agentTools.noSharedOauthApp', { provider: state.providerLabel })}
          </div>
          {!formOpen && (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="btn-outline text-[11px]"
            >
              {t('agentTools.setUpOauthApp', { provider: state.providerLabel })}
            </button>
          )}
        </>
      )}
      {formOpen && (
        <ByoForm
          state={state}
          saving={saving}
          onCancel={() => setFormOpen(false)}
          onSave={(payload) => save({ ...payload, preference: 'byo' })}
        />
      )}
      {error && <div className="text-[11px] text-destructive">{error}</div>}
    </div>
  );
}

interface ByoFormProps {
  state: ByoState;
  saving: boolean;
  onCancel: () => void;
  onSave: (payload: {
    clientId: string;
    clientSecret?: string;
    scope?: string;
  }) => void | Promise<void>;
}

function ByoForm({ state, saving, onCancel, onSave }: ByoFormProps) {
  const t = useT();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [scope, setScope] = useState('');
  const [copied, setCopied] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId.trim()) return;
    onSave({
      clientId: clientId.trim(),
      clientSecret: clientSecret || undefined,
      scope: scope || undefined,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      {state.registrationUrl && (
        <a
          href={state.registrationUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block text-[11px] underline"
        >
          {t('agentTools.openDeveloperConsole', { provider: state.providerLabel })} ↗
        </a>
      )}
      <div className="space-y-1">
        <div className="text-[11px] text-muted-foreground">{t('agentTools.redirectUri')}</div>
        <div className="flex items-center gap-1">
          <code className="font-mono text-[11px] break-all flex-1">
            {state.redirectUri}
          </code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(state.redirectUri).then(
                () => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                },
                () => {},
              );
            }}
            className="text-[11px] underline"
          >
            {copied ? t('settings.copied') : t('actions.copy')}
          </button>
        </div>
      </div>
      <label className="block text-[11px] space-y-1">
        <span className="text-muted-foreground">{t('settings.clientId')}</span>
        <input
          type="text"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          required
          autoComplete="off"
          className="input text-[11px] w-full"
        />
      </label>
      <label className="block text-[11px] space-y-1">
        <span className="text-muted-foreground">
          {t('agentTools.clientSecret')} <span className="text-muted-foreground/70">{t('agentTools.optionalForPkce')}</span>
        </span>
        <input
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          autoComplete="off"
          className="input text-[11px] w-full"
        />
      </label>
      <label className="block text-[11px] space-y-1">
        <span className="text-muted-foreground">
          {t('agentTools.scopeOverride')} <span className="text-muted-foreground/70">{t('agentTools.optionalParen')}</span>
        </span>
        <input
          type="text"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          autoComplete="off"
          className="input text-[11px] w-full"
        />
      </label>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving || !clientId.trim()}
          className="btn-primary text-[11px]"
        >
          {saving ? t('posts.savingChanges') : t('actions.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="btn-ghost text-[11px]"
        >
          {t('actions.cancel')}
        </button>
      </div>
    </form>
  );
}
